import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Drawer,
  List,
  ListItem,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  School,
  Menu as MenuIcon,
  AccountCircle,
  Settings,
  ExitToApp,
  Dashboard,
  Person,
  Notifications,
  ContentCopy,
  Check as CheckIcon,
  SwitchAccount,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import NotificationSystem from './NotificationSystem';
import { useGuestOptional } from '../context/GuestContext';
import { authService } from '../services/authService';
import TourSelectionModal from './TourSelectionModal';
import TourRunner from './TourRunner';
import { TourDefinition } from '../tours/tourDefinitions';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  userType: string;
}

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const guestContext = useGuestOptional();
  const guestId = guestContext?.guestId ?? null;
  const isGuestMode = !user && Boolean(guestId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [tourModalOpen, setTourModalOpen] = useState(false);
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null);
  const [activeTourStartStep, setActiveTourStartStep] = useState(0);
  const [tourComplete, setTourComplete] = useState(false);

  useEffect(() => {
    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Listen for storage changes and location changes to update user state
  useEffect(() => {
    const handleStorageChange = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      } else {
        setUser(null);
      }
    };

    // Listen for custom login event
    const handleLoginEvent = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    };

    // Listen for custom logout event
    const handleLogoutEvent = () => {
      setUser(null);
    };

    // Listen for storage changes
    window.addEventListener('storage', handleStorageChange);
    // Listen for custom login/logout events
    window.addEventListener('userLogin', handleLoginEvent);
    window.addEventListener('userLogout', handleLogoutEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogin', handleLoginEvent);
      window.removeEventListener('userLogout', handleLogoutEvent);
    };
  }, []);

  // Also check user state on location change
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      setUser(null);
    }
  }, [location.pathname]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchRole = async (targetRole: string) => {
    setAnchorEl(null);
    try {
      const response = await authService.switchRole(targetRole);
      window.dispatchEvent(new Event('userLogin'));
      const routes: Record<string, string> = {
        PARENT: '/parent-dashboard', STUDENT: '/student-dashboard',
        TEACHER: '/teacher-dashboard', TUTOR: '/tutor-dashboard',
      };
      navigate(routes[targetRole] || '/', { replace: true });
      if (response.user) setUser(response.user as unknown as typeof user);
    } catch (err: any) {
      if (err.status === 409 || err.message?.includes('not activated')) {
        navigate(`/role-activation?role=${targetRole}`);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setAnchorEl(null);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('userLogout'));
    navigate('/');
  };

  const handleProfileClick = () => {
    setAnchorEl(null);
    if (user?.userType === 'PARENT') {
      navigate('/parent-profile');
    } else if (user?.userType === 'TEACHER') {
      navigate('/teacher-profile');
    } else if (user?.userType === 'STUDENT') {
      navigate('/student-profile');
    } else if (user?.userType === 'TUTOR') {
      navigate('/profile-settings');
    } else {
      navigate('/profile');
    }
  };

  const handleSettingsClick = () => {
    setAnchorEl(null);
    if (user?.userType === 'PARENT') {
      navigate('/parent-settings');
    } else if (user?.userType === 'TEACHER') {
      navigate('/teacher-settings');
    } else if (user?.userType === 'STUDENT') {
      navigate('/student-settings');
    } else if (user?.userType === 'TUTOR') {
      navigate('/profile-settings');
    } else {
      navigate('/settings');
    }
  };

  const handleDashboardClick = () => {
    setAnchorEl(null);
    if (user?.userType === 'STUDENT') {
      navigate('/student-dashboard');
    } else if (user?.userType === 'PARENT') {
      navigate('/parent-dashboard');
    } else if (user?.userType === 'TEACHER') {
      navigate('/teacher-dashboard');
    } else if (user?.userType === 'TUTOR') {
      navigate('/tutor-dashboard');
    } else if (user?.userType === 'SYSTEM_ADMIN') {
      navigate('/admin/dashboard');
    }
  };

  const handleLogoClick = () => {
    if (user) {
      // Logged in users go to their dashboard
      if (user.userType === 'STUDENT') {
        navigate('/student-dashboard');
      } else if (user.userType === 'PARENT') {
        navigate('/parent-dashboard');
      } else if (user.userType === 'TEACHER') {
        navigate('/teacher-dashboard');
      } else if (user.userType === 'TUTOR') {
        navigate('/tutor-dashboard');
      } else if (user.userType === 'SYSTEM_ADMIN') {
        navigate('/admin/dashboard');
      }
    } else {
      // Not logged in users go to marketing homepage
      navigate('/');
    }
  };

  const handleCopyMcpToken = (e: React.MouseEvent) => {
    e.stopPropagation();
    const token = localStorage.getItem('authToken');
    if (token) {
      navigator.clipboard.writeText(token).then(() => {
        setTokenCopied(true);
        setTimeout(() => setTokenCopied(false), 2000);
      });
    }
  };

  const getNavigationLinks = () => {
    if (!user) {
      // Not logged in - show marketing navigation (+ Try as Guest when not already in guest flow)
      return [
        { label: 'Home', path: '/', current: location.pathname === '/' },
        { label: 'Join Us', path: '/join-us', current: location.pathname === '/join-us' },
        { label: 'About', path: '/about', current: location.pathname === '/about' },
        { label: 'Contact', path: '/contact', current: location.pathname === '/contact' }
      ];
    }

    // Logged in - show user-type specific navigation
    switch (user.userType) {
      case 'STUDENT':
        return [
          { label: 'Start', path: '/student-dashboard', current: location.pathname === '/student-dashboard' },
          { label: 'My Courses', path: '/student-courses', current: location.pathname.startsWith('/student-courses') },
          { label: 'Find courses', path: '/browse-courses', current: location.pathname === '/browse-courses' },
          { label: 'Deadlines', path: '/student-deadlines', current: location.pathname.startsWith('/student-deadlines') },
          { label: 'Tactical', path: '/student-tactical', current: location.pathname.startsWith('/student-tactical') },
          { label: 'Accolades', path: '/student-accolades', current: location.pathname.startsWith('/student-accolades') },
          { label: 'Insights', path: '/student-insights', current: location.pathname.startsWith('/student-insights') },
          { label: 'Activities', path: '/student-activities', current: location.pathname.startsWith('/student-activities') }
        ];

      case 'PARENT':
        return [
          { label: 'Dashboard', path: '/parent-dashboard', current: location.pathname === '/parent-dashboard' },
          { label: 'Courses', path: '/parent-courses', current: location.pathname === '/parent-courses' },
          { label: 'Find courses', path: '/browse-courses', current: location.pathname === '/browse-courses' },
          { label: 'Progress Reports', path: '/parent-progress-reports', current: location.pathname === '/parent-progress-reports' },
          { label: 'Achievements', path: '/parent-achievements', current: location.pathname === '/parent-achievements' },
          { label: 'Messages', path: '/parent-messages', current: location.pathname.startsWith('/parent-messages') || location.pathname.startsWith('/parent/messages') }
        ];

      case 'TEACHER':
        return [
          { label: 'Dashboard', path: '/teacher-dashboard', current: location.pathname === '/teacher-dashboard' },
          { label: 'Classes', path: '/teacher-classes', current: location.pathname.startsWith('/teacher-classes') },
          { label: 'Students', path: '/teacher-students', current: location.pathname.startsWith('/teacher-students') },
          { label: 'Messages', path: '/teacher/messages', current: location.pathname.startsWith('/teacher/messages') },
          { label: 'Reports', path: '/teacher-reports', current: location.pathname.startsWith('/teacher-reports') }
        ];

      case 'TUTOR':
        return [
          { label: 'Dashboard', path: '/tutor-dashboard', current: location.pathname === '/tutor-dashboard' },
          { label: 'My Courses', path: '/tutor/courses', current: location.pathname.startsWith('/tutor/courses') }
        ];

      case 'SYSTEM_ADMIN':
        return [
          { label: 'Dashboard', path: '/admin/dashboard', current: location.pathname === '/admin/dashboard' },
          // Topics management is subject-based at /admin/topics; also highlight when in any topics-related route
          { label: 'Subjects & Topics', path: '/admin/subjects', current: location.pathname === '/admin/subjects' || location.pathname.includes('/subjects') },
          { label: 'Boards & Curriculum', path: '/admin/curricula', current: location.pathname.startsWith('/admin/curricula') },
          { label: 'Courses', path: '/admin/courses', current: location.pathname === '/admin/courses' || (location.pathname.startsWith('/admin/courses/') && !location.pathname.includes('/topics')) },
          { label: 'Users', path: '/admin/users', current: location.pathname === '/admin/users' },
          { label: 'Locations', path: '/admin/locations', current: location.pathname === '/admin/locations' },
          { label: 'Schools', path: '/admin/schools', current: location.pathname === '/admin/schools' }
        ];

      default:
        return [
          { label: 'Dashboard', path: '/student-dashboard', current: location.pathname === '/student-dashboard' }
        ];
    }
  };

  const getUserInitials = (user: User) => {
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  };

  const getUserDisplayName = (user: User) => {
    return `${user.firstName} ${user.lastName}`;
  };

  const getUserRoleDisplay = (userType: string) => {
    const roleMap: { [key: string]: string } = {
      'STUDENT': 'Student',
      'PARENT': 'Parent',
      'TEACHER': 'Teacher',
      'TUTOR': 'Tutor',
      'SCHOOL_ADMIN': 'School Admin',
      'SYSTEM_ADMIN': 'System Admin',
    };
    return roleMap[userType] || userType;
  };

  return (
    <AppBar position="static" elevation={0} sx={{ backgroundColor: 'white', color: 'text.primary' }}>
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          {/* Logo and Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 0, md: 4 }, flexShrink: 0 }}>
            <Box
              component={!user ? Link : 'div'}
              {...(!user ? { to: '/' } : {})}
              onClick={handleLogoClick}
              sx={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: 'inherit',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              <School sx={{ mr: 1, color: 'primary.main', fontSize: { xs: 28, sm: 32 } }} />
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.15rem', sm: '1.4rem' },
                    color: 'primary.main',
                    letterSpacing: '-0.5px',
                  }}
                >
                  Study
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.15rem', sm: '1.4rem' },
                    color: 'secondary.main',
                    letterSpacing: '-0.5px',
                  }}
                >
                  Stride
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Navigation Links - Hidden on mobile */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
              {getNavigationLinks().map((link) => (
                <Button
                  key={link.label}
                  component={Link}
                  to={link.path}
                  color="inherit"
                  sx={{
                    fontWeight: 500,
                    backgroundColor: link.current ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.05)',
                    }
                  }}
                >
                  {link.label}
                </Button>
              ))}
              {user?.userType === 'TUTOR' && (
                <Button
                  color="inherit"
                  onClick={() => setTourModalOpen(true)}
                  sx={{ fontWeight: 500 }}
                >
                  How It Works
                </Button>
              )}
            </Box>
          )}

          {/* Action Buttons + mobile menu (right-aligned on mobile) */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              ml: { xs: 'auto', md: 0 },
              flexShrink: 0,
            }}
          >
            {user ? (
              /* User is logged in - show user menu and logout button */
              <>
                {/* Notifications */}
                <NotificationSystem userType={user.userType as 'parent' | 'teacher' | 'student'} />

                {/* Quick Action Icons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="Profile">
                    <IconButton
                      onClick={handleProfileClick}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(25, 118, 210, 0.1)',
                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.2)' }
                      }}
                    >
                      <Person />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Settings">
                    <IconButton
                      onClick={handleSettingsClick}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(25, 118, 210, 0.1)',
                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.2)' }
                      }}
                    >
                      <Settings />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Logout">
                    <IconButton
                      onClick={handleLogout}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                        color: 'error.main',
                        '&:hover': {
                          bgcolor: 'rgba(244, 67, 54, 0.2)',
                          color: 'error.dark'
                        }
                      }}
                    >
                      <ExitToApp />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={`${user.firstName} ${user.lastName}`}>
                    <IconButton
                      onClick={handleProfileMenuOpen}
                      size="small"
                      sx={{ ml: 1 }}
                      aria-controls={Boolean(anchorEl) ? 'profile-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {getUserInitials(user)}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                </Box>


                {/* Profile Menu */}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleProfileMenuClose}
                  onClick={handleProfileMenuClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem disabled>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {getUserInitials(user)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {getUserDisplayName(user)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {user.email}
                      </Typography>
                      <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 600, mt: 0.5 }}>
                        {getUserRoleDisplay(user.userType)}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />
                  {user && ['PARENT', 'STUDENT'].filter(r => r !== user.userType).map(targetRole => (
                    <MenuItem key={targetRole} onClick={() => handleSwitchRole(targetRole)}>
                      <ListItemIcon><SwitchAccount fontSize="small" /></ListItemIcon>
                      <ListItemText>Switch to {targetRole.charAt(0) + targetRole.slice(1).toLowerCase()}</ListItemText>
                    </MenuItem>
                  ))}
                  {(user?.userType === 'TUTOR' || user?.userType === 'TEACHER') && (
                    <MenuItem onClick={() => navigate('/profile-settings')}>
                      <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                      <ListItemText>Profile Settings</ListItemText>
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem onClick={handleCopyMcpToken}>
                    <ListItemIcon>
                      {tokenCopied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText>
                      {tokenCopied ? 'Token Copied!' : 'Copy Token for MCP'}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              /* User is not logged in: Try as Guest (when not already in guest), then Login, Register */
              <>
                {!location.pathname.startsWith('/guest') && (
                  <Button
                    component={Link}
                    to="/guest"
                    variant="outlined"
                    color="secondary"
                    sx={{ display: { xs: 'none', md: 'inline-flex' }, fontWeight: 500 }}
                  >
                    Try as Guest
                  </Button>
                )}
                <Button
                  component={Link}
                  to="/login"
                  variant="outlined"
                  color="primary"
                  sx={{ fontWeight: 500 }}
                >
                  Login
                </Button>
                <Button
                  component={Link}
                  to="/signup"
                  variant="contained"
                  color="primary"
                  sx={{ fontWeight: 500 }}
                >
                  Register
                </Button>
              </>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="Open menu"
                onClick={() => setMobileMenuOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile drawer: nav links + Try as Guest, Login, Register */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ py: 2, px: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Menu
          </Typography>
          <List disablePadding>
            {getNavigationLinks().map((link) => (
              <ListItem key={link.label} disablePadding sx={{ mb: 1 }}>
                <Button
                  fullWidth
                  component={Link}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  color="inherit"
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {link.label}
                </Button>
              </ListItem>
            ))}
            {user?.userType === 'TUTOR' && (
              <ListItem disablePadding sx={{ mb: 1 }}>
                <Button
                  fullWidth
                  color="inherit"
                  onClick={() => { setTourModalOpen(true); setMobileMenuOpen(false); }}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  How It Works
                </Button>
              </ListItem>
            )}
          </List>
          <Divider sx={{ my: 2 }} />
          {!user ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {!location.pathname.startsWith('/guest') && (
                <Button
                  component={Link}
                  to="/guest"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Try as Guest
                </Button>
              )}
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                color="primary"
                fullWidth
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Button>
              <Button
                component={Link}
                to="/signup"
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => setMobileMenuOpen(false)}
              >
                Register
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                fullWidth
                onClick={() => { handleProfileClick(); setMobileMenuOpen(false); }}
              >
                Profile
              </Button>
              <Button
                fullWidth
                onClick={() => { handleSettingsClick(); setMobileMenuOpen(false); }}
              >
                Settings
              </Button>
              <Button
                fullWidth
                color="error"
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              >
                Logout
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Tour system — TUTOR only */}
      {user?.userType === 'TUTOR' && (
        <TourSelectionModal
          open={tourModalOpen}
          onClose={() => setTourModalOpen(false)}
          onSelectTour={(tour, startStep) => {
            setActiveTour(tour);
            setActiveTourStartStep(startStep ?? 0);
            setTourModalOpen(false);
          }}
          userId={user.id}
        />
      )}

      <TourRunner
        tour={activeTour}
        userId={user?.id ?? 0}
        startStep={activeTourStartStep}
        onComplete={() => { setActiveTour(null); setTourComplete(true); }}
        onExit={() => setActiveTour(null)}
      />

      <Snackbar
        open={tourComplete}
        autoHideDuration={4000}
        onClose={() => setTourComplete(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setTourComplete(false)} severity="success" sx={{ width: '100%' }}>
          Tour complete! You can relaunch it any time from "How It Works".
        </Alert>
      </Snackbar>
    </AppBar>
  );
};

export default Header;
